import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { euroInt } from "@/lib/format";
import { generatePosterPdf } from "@/lib/pdfLazy";
import { compressImage } from "@/lib/compressImage";
import { Button } from "@/components/ui/button";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Loader2, Trash2, Store, FileText, Printer, ImagePlus, RotateCcw, Crop, Pencil, AlertTriangle } from "lucide-react";
import { CropDialog } from "@/components/CropDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/context/I18nContext";
import { localizeSalon } from "@/lib/i18n";
import { UsedVanDialog, localizePoster, usedLine } from "@/components/UsedVanDialog";
import { QuickUsedPrice } from "@/components/QuickUsedPrice";
import { Badge } from "@/components/ui/badge";
import { VanIcon } from "@/components/VanIcon";
import { PosterPreview } from "@/components/PosterPreview";

const ALL = "__all__";

export default function ExpoManager() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [expos, setExpos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(null);
  const [cropFor, setCropFor] = useState(null);
  const [usedDlg, setUsedDlg] = useState(null);
  const [place, setPlace] = useState(ALL);
  const [search, setSearch] = useState("");
  const places = [...new Set(expos.map((e) => e.name).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr"));
  const [kind, setKind] = useState(ALL);
  const isUsed = (e) => e.range_key === "occasion";
  const [country, setCountry] = useState(ALL);
  const [countryNames, setCountryNames] = useState({});
  useEffect(() => {
    if (isAdmin) api.get("/countries").then(({ data }) => setCountryNames(Object.fromEntries((data || []).map((c) => [c.code, c.name])))).catch(() => {});
  }, [isAdmin]);
  const ctryOf = (e) => (e.country_code || "FR").toUpperCase();
  const saveSerial = async (e, val) => {
    if ((e.serial_number || "") === val.trim()) return;
    try {
      const { data } = await api.put(`/expos/${e.id}/serial`, { serial_number: val });
      setExpos((xs) => xs.map((x) => (x.id === e.id ? data : x)));
      toast.success(t("Numéro de série enregistré"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setExpoCountry = async (e, code) => {
    try {
      const { data } = await api.put(`/expos/${e.id}/country`, { country_code: code });
      setExpos((xs) => xs.map((x) => (x.id === e.id ? data : x)));
      toast.success(`${t("Pays modifié")} : ${t(countryNames[code] || code)}`);
      if (e.range_key !== "occasion") {
        toast.info(t("Recalcul du prix et de l'affiche…"));
        localStorage.removeItem("fautras_client"); localStorage.removeItem("fautras_draft_id"); localStorage.removeItem("fautras_client_id");
        nav(`/configurer?expo=${e.id}&reprice=1`);
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const ctries = [...new Set(expos.map(ctryOf))].sort((a, b) => (countryNames[a] || a).localeCompare(countryNames[b] || b, "fr"));
  const byCountry = country === ALL ? expos : expos.filter((e) => ctryOf(e) === country);
  const byKind = kind === ALL ? byCountry : byCountry.filter((e) => (kind === "used") === isUsed(e));
  const byPlace = place === ALL ? byKind : byKind.filter((e) => e.name === place);
  const term = search.trim().toLowerCase();
  const shown = !term ? byPlace : byPlace.filter((e) => [e.serial_number, e.name, e.model_name, e.poster?.model_name_short].some((v) => (v || "").toLowerCase().includes(term)));

  const loadExpos = () => {
    setLoading(true);
    api.get("/expos").then(({ data }) => setExpos(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(loadExpos, []);

  const remove = async (id) => {
    try { await api.delete(`/expos/${id}`); toast.success(isAdmin ? "Véhicule supprimé du stock (restaurable 30 jours depuis Admin › Corbeille du stock)" : "Véhicule supprimé du stock (restaurable 30 jours par Fautras)", { duration: 7000 }); loadExpos(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const restock = async (id) => {
    try { await api.post(`/expos/${id}/restock`); toast.success("Occasion remise en stock"); loadExpos(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const setPhoto = async (e, image) => {
    setPhotoBusy(e.id);
    try {
      const { data } = await api.put(`/expos/${e.id}/image`, { image });
      setExpos((list) => list.map((x) => (x.id === e.id ? data : x)));
      toast.success(image ? "Photo modifiée" : "Photo d'origine rétablie");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setPhotoBusy(null); }
  };
  const uploadPhoto = async (e, ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    setPhotoBusy(e.id);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(f));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await setPhoto(e, data.url);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec du téléversement"); setPhotoBusy(null); }
  };
  const [posterPdf, setPosterPdf] = useState(null);
  const reprint = async (e) => {
    setPrinting(e.id);
    try { setPosterPdf(await generatePosterPdf(localizePoster(e.poster, e.used), { preview: true })); } catch (err) { toast.error("Impossible de générer l'affiche"); }
    finally { setPrinting(null); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-primary">{t("Stock disponible")}</div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <VanIcon className="w-7 h-7" /> {t("Véhicules à la vente")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t("Pour mettre un véhicule en stock, configurez un modèle dans le")} <Link to="/" className="text-primary underline" data-testid="expo-goto-config">{t("configurateur")}</Link> {t("puis cliquez sur « Mettre en stock et créer une affiche prix ».")}
        </p>
        <Button variant="outline" className="mt-3 gap-2" onClick={() => nav("/configurer?range=occasion&new=1")} data-testid="expo-add-used">
          <Store className="w-4 h-4" /> {t("Mettre en stock une occasion")}
        </Button>
        {isAdmin && expos.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{t("Pays :")}</span>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="w-full sm:w-56" data-testid="expo-filter-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} data-testid="expo-filter-country-all">{t("Tous les pays")} ({expos.length})</SelectItem>
                {ctries.map((c) => <SelectItem key={c} value={c} data-testid={`expo-filter-country-${c}`}>{t(countryNames[c] || c)} ({expos.filter((e) => ctryOf(e) === c).length})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {expos.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{t("Rechercher :")}</span>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("N° de série, modèle, lieu…")}
              className="w-full sm:w-72 h-9" data-testid="expo-search" />
            {term && <span className="text-xs text-muted-foreground" data-testid="expo-search-count">{shown.length} {t("résultat(s)")}</span>}
          </div>
        )}
        {expos.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{t("Type :")}</span>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="w-full sm:w-56" data-testid="expo-filter-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} data-testid="expo-filter-kind-all">{t("Neuf et occasion")} ({byCountry.length})</SelectItem>
                <SelectItem value="new" data-testid="expo-filter-kind-new">{t("Neuf")} ({byCountry.filter((e) => !isUsed(e)).length})</SelectItem>
                <SelectItem value="used" data-testid="expo-filter-kind-used">{t("Occasion")} ({byCountry.filter(isUsed).length})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {places.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{t("Lieu de stockage / événement :")}</span>
            <Select value={place} onValueChange={setPlace}>
              <SelectTrigger className="w-full sm:w-72" data-testid="expo-filter-place"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("Tous")} ({byKind.length})</SelectItem>
                {places.map((p) => <SelectItem key={p} value={p}>{p} ({byKind.filter((e) => e.name === p).length})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : expos.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="expo-empty">{t("Aucun véhicule en stock pour le moment.")}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((e) => (
            <div key={e.id} className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col" data-testid={`expo-card-${e.id}`}>
              <div className="relative">
                {e.poster?.model_image ? <img src={e.poster.model_image} alt={e.model_name} className="w-full aspect-[4/3] object-cover" data-testid={`expo-photo-${e.id}`} />
                  : <div className="w-full aspect-[4/3] bg-muted" />}
                {e.range_key === "occasion" && <Badge className="absolute top-2 left-2 bg-[#9E7B41] hover:bg-[#9E7B41] text-white" data-testid={`expo-used-badge-${e.id}`}>{t("Occasion")}</Badge>}
                {e.sold_quote_id && (
                  <div className="absolute inset-0 bg-black/45 flex items-center justify-center" data-testid={`expo-sold-${e.id}`}>
                    <span className="bg-destructive text-white font-bold uppercase tracking-widest px-4 py-1.5 rounded -rotate-6 shadow">{t("Vendu")}{e.sold_quote_number ? ` · ${e.sold_quote_number}` : ""}</span>
                  </div>
                )}
                {(isAdmin || e.created_by === user?.id) && (
                  <div className="absolute bottom-2 right-2 flex gap-1.5">
                    <input type="file" accept="image/*" className="hidden" id={`expo-photo-file-${e.id}`} onChange={(ev) => uploadPhoto(e, ev)} data-testid={`expo-photo-input-${e.id}`} />
                    <Button asChild size="sm" variant="secondary" className="gap-1.5 h-8 shadow" disabled={photoBusy === e.id}>
                      <label htmlFor={`expo-photo-file-${e.id}`} className="cursor-pointer" data-testid={`expo-photo-change-${e.id}`}>
                        {photoBusy === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />} {t("Changer la photo")}
                      </label>
                    </Button>
                    {e.poster?.model_image && (
                      <Button size="sm" variant="secondary" className="gap-1.5 h-8 shadow" disabled={photoBusy === e.id} onClick={() => setCropFor(e)} data-testid={`expo-photo-crop-${e.id}`}>
                        <Crop className="w-3.5 h-3.5" /> {t("Recadrer")}
                      </Button>
                    )}
                    {e.poster?.original_image != null && e.poster.model_image !== e.poster.original_image && (
                      <Button size="sm" variant="secondary" className="gap-1.5 h-8 shadow" disabled={photoBusy === e.id} onClick={() => setPhoto(e, "")} data-testid={`expo-photo-reset-${e.id}`}>
                        <RotateCcw className="w-3.5 h-3.5" /> {t("Photo d'origine")}
                      </Button>
                    )}
                  </div>
                )}
              </div>
              <div className="p-4 flex flex-col flex-1">
                <div className="font-display text-lg font-bold">{localizeSalon(e.name, lang)}</div>
                <div className="text-sm text-muted-foreground mt-0.5">{t(e.range_name)} · {t(e.poster?.model_name_short || e.model_name)}</div>
                {e.source_unsigned && <div className="mt-1.5 flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900" data-testid={`expo-unsigned-warning-${e.id}`}><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{t("Reprise d'un devis non signé : la commande du neuf n'est pas encore signée.")}</div>}
                {e.range_key === "occasion" && e.poster?.used_line && <div className="text-xs text-muted-foreground mt-0.5" data-testid={`expo-used-line-${e.id}`}>{(e.used ? usedLine(e.used) : e.poster.used_line).split(" · ").slice(1).join(" · ")} · {e.used?.vat_recoverable ? t("TVA récupérable") : t("Sans TVA")}</div>}
                {e.range_key === "occasion" && !(Number(e.poster?.total_salon_ttc) > 0) ? (
                  <div className="font-semibold text-amber-700 mt-1" data-testid={`expo-price-${e.id}`}>{t("Prix à définir")}</div>
                ) : e.poster?.total_salon_ttc != null && (
                  <div className="font-mono text-[#9E7B41] font-semibold mt-1" data-testid={`expo-price-${e.id}`}>{euroInt(e.poster.total_salon_ttc)} {t("TTC")} <span className="text-[10px] font-sans uppercase tracking-wide">{t("Prix stock")}</span></div>
                )}
                {e.range_key === "occasion" && !e.sold_quote_id && (isAdmin || e.created_by === user?.id) && <QuickUsedPrice expo={e} t={t} onSaved={loadExpos} />}
                {(isAdmin || e.created_by === user?.id) ? (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs font-medium whitespace-nowrap">{t("N° de série")}</span>
                    <Input defaultValue={e.serial_number || ""} key={`${e.id}-${e.serial_number || ""}`} placeholder={t("À saisir")}
                      onBlur={(ev) => saveSerial(e, ev.target.value)} onKeyDown={(ev) => { if (ev.key === "Enter") ev.currentTarget.blur(); }}
                      className="h-8 text-xs font-mono" data-testid={`expo-serial-${e.id}`} />
                  </div>
                ) : e.serial_number ? (
                  <div className="text-xs mt-1" data-testid={`expo-serial-text-${e.id}`}>{t("N° de série")} : <span className="font-mono">{e.serial_number}</span></div>
                ) : null}
                <div className="text-xs text-muted-foreground mt-1">
                  {e.range_key === "occasion" ? t("Options en texte libre") : `${(e.options || []).length} ${t("option(s)")}`}{e.poster?.promo_names?.length ? ` · ${t("Promo")} : ${e.poster.promo_names.join(", ")}` : e.promo_name ? ` · ${t("Promo")} : ${e.promo_name}` : ""}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {(e.reseller_ids || []).length ? `${t("Réservé à")} ${(e.reseller_ids || []).length} ${t("revendeur(s)")}` : t("Visible par tous les revendeurs du pays")}
                  {e.created_by_name ? ` · ${t("par")} ${e.created_by_name}` : ""}
                </div>
                {isAdmin && Object.keys(countryNames).length > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs font-medium">{t("Pays :")}</span>
                    <Select value={ctryOf(e)} onValueChange={(v) => setExpoCountry(e, v)}>
                      <SelectTrigger className="h-8 text-xs w-44" data-testid={`expo-country-${e.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(countryNames).sort((a, b) => t(a[1]).localeCompare(t(b[1]))).map(([c, n]) => <SelectItem key={c} value={c} data-testid={`expo-country-${e.id}-${c}`}>{t(n)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-auto pt-4">
                  <Button onClick={() => { localStorage.removeItem("fautras_client"); localStorage.removeItem("fautras_draft_id"); localStorage.removeItem("fautras_client_id"); nav(e.range_key === "occasion" ? `/configurer?range=occasion&model=${e.id}` : `/configurer?expo=${e.id}`); }} className="gap-2 flex-1" disabled={!!e.sold_quote_id || (e.range_key === "occasion" && !(Number(e.poster?.total_salon_ttc) > 0))} title={e.range_key === "occasion" && !(Number(e.poster?.total_salon_ttc) > 0) ? t("Renseignez le prix de vente pour faire un devis") : undefined} data-testid={`expo-open-${e.id}`}>
                    <FileText className="w-4 h-4" /> {t("Voir le devis")}
                  </Button>
                  <Button variant="outline" size="icon" disabled={!e.poster?.model_name || printing === e.id || (e.range_key === "occasion" && !(Number(e.poster?.total_salon_ttc) > 0))} onClick={() => reprint(e)} title={t("Réimprimer l'affiche")} data-testid={`expo-print-${e.id}`}>
                    {printing === e.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                  </Button>
                  {e.sold_quote_id && isAdmin && (
                    <Button variant="outline" size="icon" onClick={() => restock(e.id)} title={t("Remettre en stock")} data-testid={`expo-restock-${e.id}`}>
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  )}
                  {e.range_key === "occasion" && !e.sold_quote_id && (isAdmin || e.created_by === user?.id) && (
                    <Button variant="outline" size="icon" onClick={() => setUsedDlg({ existing: e })} title={t("Modifier")} data-testid={`expo-used-edit-${e.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                  {(isAdmin || e.created_by === user?.id) && (
                    <ConfirmDelete label={e.range_key === "occasion" ? "cette occasion du stock" : "ce véhicule du stock"} name={e.name} onConfirm={() => remove(e.id)} testid={`expo-delete-${e.id}`}
                      note={isAdmin ? "Le véhicule disparaîtra du stock. Il reste restaurable pendant 30 jours depuis Admin › Corbeille du stock." : "Le véhicule disparaîtra du stock. En cas d'erreur, Fautras peut le restaurer pendant 30 jours."}>
                      <Button variant="ghost" size="icon" className="text-destructive" data-testid={`expo-delete-${e.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </ConfirmDelete>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <UsedVanDialog open={!!usedDlg} onOpenChange={(v) => { if (!v) setUsedDlg(null); }} existing={usedDlg?.existing} onSaved={loadExpos} />
      <CropDialog open={!!cropFor} onOpenChange={(v) => !v && setCropFor(null)} uploadPath="/upload"
        src={cropFor?.poster?.model_image || ""}
        onCropped={(url) => setPhoto(cropFor, url)} />
      <PosterPreview pdf={posterPdf} onClose={() => setPosterPdf(null)} printable />
    </div>
  );
}
